import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import { PageMeta } from '../../components/common/PageMeta'
import { ShareExperienceForm } from './ShareExperienceForm'
import { OwnersIntro } from './OwnersIntro'
import { GuestReviews } from '../../components/reviews/GuestReviews'

/**
 * About the estate, and what people who keep coming back say about it.
 *
 * <p>The testimonials are the point of the page. Everything above them is context for reading
 * them, kept short: a visitor who has reached this page is deciding whether to trust the
 * place, and six paragraphs of atmosphere is not what decides that.
 */
export function AboutPage() {
  const { t } = useTranslation()


  return (
    <Container sx={{ py: { xs: 6, md: 10 } }}>
      <PageMeta title={t('about.title')} path="/about" />

      <Typography variant="h1" sx={{ mb: 2 }}>
        {t('about.title')}
      </Typography>
      <Typography variant="h5" component="p" color="text.secondary" sx={{ mb: 5, maxWidth: 720 }}>
        {t('about.lede')}
      </Typography>

      {/* Who "us" is comes first. A page called About us that opens with three cards about
          the place, and only mentions the people who run it further down, is answering the
          wrong question. */}
      <Box sx={{ mb: { xs: 6, md: 8 } }}>
        <OwnersIntro />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          mb: { xs: 6, md: 8 },
        }}
      >
        {(['family', 'lake', 'kitchen'] as const).map((key) => (
          <Card key={key} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 3 }}>
              <Box aria-hidden sx={{ fontSize: '1.75rem', mb: 1, lineHeight: 1 }}>
                {{ family: '🏡', lake: '🛶', kitchen: '🍲' }[key]}
              </Box>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {t(`about.${key}Title`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`about.${key}Body`)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <GuestReviews />

      <Box sx={{ mt: { xs: 6, md: 8 } }}>
        <ShareExperienceForm />
      </Box>
    </Container>
  )
}
